import React, { useState } from 'react';
import { Monitor, Plus, Clock, CheckCircle, Upload } from 'lucide-react';

const ITDepartmentPage = () => {
  const [activeTab, setActiveTab] = useState('submit');
  const [tickets, setTickets] = useState([
    { id: 1, title: "Laptop not booting", status: "Pending", date: "2025-05-20" },
    { id: 2, title: "Printer not connecting", status: "Resolved", date: "2025-05-19" },
  ]);
  const [formData, setFormData] = useState({ title: '', description: '', category: 'Hardware' });
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description) {
      alert("Please fill Title and Description");
      return;
    }

    const newTicket = {
      id: Date.now(),
      title: formData.title,
      status: "Pending",
      date: new Date().toISOString().split('T')[0],
    };

    setTickets([newTicket, ...tickets]);
    alert("✅ Ticket submitted successfully!");
    setFormData({ title: '', description: '', category: 'Hardware' });
    setUploadedFiles([]);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    setUploadedFiles([...uploadedFiles, ...files]);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center gap-4 mb-8">
        <div className="w-16 h-16 bg-cyan-100 rounded-2xl flex items-center justify-center">
          <Monitor size={40} className="text-cyan-600" />
        </div>
        <div>
          <h1 className="text-4xl font-bold text-gray-900">IT Department</h1>
          <p className="text-gray-600">Support • Infrastructure • Systems</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-8 bg-white rounded-t-2xl">
        {[
          { id: 'submit', label: 'Submit Ticket', icon: Plus },
          { id: 'my-tickets', label: 'My Tickets', icon: Clock },
          { id: 'knowledge', label: 'Knowledge Base', icon: CheckCircle },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-medium border-b-4 transition-all ${
              activeTab === tab.id 
                ? 'border-custom-orange text-custom-orange' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon size={20} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Submit Ticket Tab */}
      {activeTab === 'submit' && (
        <div className="grid md:grid-cols-2 gap-8">
          {/* Form */}
          <div className="bg-white rounded-3xl shadow p-8">
            <h2 className="text-2xl font-semibold mb-6">Submit New Ticket</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Issue Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full border rounded-xl px-4 py-3 focus:outline-none focus:border-custom-orange"
                  placeholder="e.g. Can't connect to WiFi"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full border rounded-xl px-4 py-3 focus:outline-none focus:border-custom-orange"
                >
                  <option>Hardware</option>
                  <option>Software</option>
                  <option>Network</option>
                  <option>Access / Login</option>
                  <option>Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={6}
                  className="w-full border rounded-xl px-4 py-3 focus:outline-none focus:border-custom-orange"
                  placeholder="Please describe the issue in detail..."
                  required
                />
              </div>

              {/* Drag & Drop */}
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleFileDrop}
                className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center hover:border-custom-orange transition cursor-pointer"
              >
                <Upload size={40} className="mx-auto text-gray-400 mb-3" />
                <p className="font-medium">Drag & drop screenshots or files here</p>
                <p className="text-sm text-gray-500">or click to browse</p>
                {uploadedFiles.length > 0 && (
                  <p className="text-green-600 mt-3">✓ {uploadedFiles.length} file(s) attached</p>
                )}
              </div>

              <button
                type="submit"
                className="w-full bg-custom-orange hover:bg-orange-600 text-white font-semibold py-4 rounded-xl transition text-lg"
              >
                Submit Ticket
              </button>
            </form>
          </div>

          {/* Live Queue */}
          <div>
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Clock size={20} /> Live Ticket Queue
            </h3>
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <div key={ticket.id} className="bg-white border rounded-2xl p-5 flex justify-between items-center">
                  <div>
                    <p className="font-medium">{ticket.title}</p>
                    <p className="text-xs text-gray-500">{ticket.date}</p>
                  </div>
                  <span className={`px-4 py-1 rounded-full text-xs font-medium ${
                    ticket.status === 'Resolved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {ticket.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Other Tabs */}
      {activeTab === 'my-tickets' && (
        <div className="bg-white rounded-3xl shadow p-10 text-center">
          <h2 className="text-3xl font-semibold mb-4">My Tickets</h2>
          <p className="text-gray-500">Your submitted tickets will appear here.</p>
        </div>
      )}

      {activeTab === 'knowledge' && (
        <div className="bg-white rounded-3xl shadow p-10 text-center">
          <h2 className="text-3xl font-semibold mb-4">Knowledge Base</h2>
          <p className="text-gray-500">Helpful guides and common solutions coming soon.</p>
        </div>
      )}
    </div>
  );
};

export default ITDepartmentPage;